import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { formatBaht } from '@/lib/money'
import { useForm, Controller } from 'react-hook-form'
import { Loader2, FileText } from 'lucide-react'
import { Button, Card, DateInput, FormActions, Input, SearchableDropdown } from '@/components/ui'
import { IdCopyUpload } from './IdCardBlackout'
import { PreFilledBadge } from '@/components/ui/PreFilledBadge'
import { useCreateLease, useUpdateLease } from '@/hooks/useLeases'
import { useProperties } from '@/hooks/useProperties'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useCustomers, useCustomer } from '@/hooks/useCustomers'
import { usePropertyUnits, useUnit } from '@/hooks/useUnits'
import { useDocumentTemplates } from '@/hooks/useDocumentTemplates'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'
// WEB-H7: PDF generators are dynamic-imported at the call sites (pdfme weight).
import { api } from '@/lib/api'
import { customerLabel } from '@/lib/customerLabel'
import { localToday } from '@/lib/date'
import type { Property, Unit, Lease, Customer, ApiResponse } from '@/types'
import type { Template } from '@pdfme/common'
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning'
import { useFormTelemetry } from '@/hooks/useFormTelemetry'
import { removedInSession } from '@/lib/sessionRedaction'
import { SupportSessionHidden } from '@/components/SupportSessionHidden'

// Owner 2026-10-08: the key-card deposit a new lease starts with (฿).
export const DEFAULT_KEYCARD_DEPOSIT = 200

// ---------------------------------------------------------------------------
// File upload helper (same logic as LeaseFormPage)
// ---------------------------------------------------------------------------
async function uploadLeaseFile(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  return `/api/images/${presigned.key}`
}

// ---------------------------------------------------------------------------
// Internal form shape
// ---------------------------------------------------------------------------
interface LeaseFormData {
  unit_id: number
  customer_id: number
  started_at: string
  ended_at: string
  deposit: number
  // T156-010 (EP-156 §3.3) — two optional, invoicing-independent lease
  // charges. '' on the form means "not set", sent to the server as null.
  move_in_fee_amount: string
  keycard_deposit_amount: string
}

// DAT-1: build + read entirely in local fields, never a toISOString round
// trip: `new Date(dateStr)` on a date-only string anchors at UTC midnight,
// and converting back via toISOString shifts the calendar day whenever the
// runtime's system zone isn't UTC.
function addOneYear(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  date.setFullYear(date.getFullYear() + 1)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// T156-010 (EP-156 §3.3) — the form keeps both optional charges as strings so
// an empty box and a genuine 0 stay distinguishable; this is the one place
// that collapses '' to null before the field reaches a mutation payload.
function parseOptionalAmount(value: string): number | null {
  return value.trim() === '' ? null : Number(value)
}

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------
export interface LeaseFieldsProps {
  /**
   * Null → create mode (POST /api/units/:id/leases).
   * Non-null → edit mode (PATCH /api/leases/:id).
   */
  lease: Lease | null
  /** Pre-selects the unit dropdown on create (ignored in edit mode). */
  defaultUnitId?: number
  /**
   * Pre-selects the PROPERTY on create without pre-picking a unit (ignored in
   * edit mode). The onboarding wizard passes this so the property is fixed
   * (chosen in step 1) while the unit dropdown auto-selects only when there's a
   * single vacant unit — otherwise the operator must pick from the vacant units.
   */
  defaultPropertyId?: number
  /** Pre-selects the customer dropdown on create (ignored in edit mode). */
  defaultCustomerId?: number
  /** Called with the saved Lease after a successful POST or PATCH. */
  onSave: (saved: Lease) => void
  /** Optional cancel handler — when provided, a Cancel button renders beside Save. */
  onCancel?: () => void
  /**
   * Optional HTML form id. When set, the <form> element receives this id so an
   * external <button form={formId} type="submit"> can trigger submission.
   */
  formId?: string
  /**
   * EP-86 S2: When true, Essentials fields render inline and Advanced options
   * (Terms: ended_at/deposit; Documents: uploads) start collapsed behind a
   * toggle button. Every Advanced field keeps a valid default so a collapsed
   * Advanced section never blocks submission (S2-2).
   *
   * When false (default), all sections are always visible — standalone
   * /leases/new and /:id/edit pages pass nothing or false.
   *
   * NOT piggybacked on formId (S2-1): formId means "external submit button",
   * not "streamlined"; they're orthogonal.
   */
  streamlinedMode?: boolean
  /**
   * Apply-from-file banner node (task-030: ApplyFromFileButton).
   * Rendered at the very top of the card, before the form.
   * Pass null or omit to show nothing.
   */
  prefillBanner?: ReactNode
  /**
   * When provided, resets the RHF form with these values so the fields
   * display the AI-parsed data (task-030 Apply path).
   * Only non-null values from this object override the existing defaults.
   */
  prefillDefaults?: Partial<LeaseFormData>
  className?: string
  /** Fires with the mutation-pending state so the wizard footer Save can spin. */
  onSubmittingChange?: (submitting: boolean) => void
}

/**
 * Self-contained lease form.
 *
 * Owns its own useForm instance and mutation hooks so it can be embedded in
 * any context: LeaseFormPage, the onboarding wizard Step 5, or a quick-add
 * surface. The parent is responsible only for navigation / closing.
 *
 * Create path:  POST /api/units/:unitId/leases → generates PDF → calls onSave.
 * Edit path:    PATCH /api/leases/:id → calls onSave.
 *
 * EP-86 S2 — Sections:
 *   Essentials (always visible): customer, property+unit, rent display, started_at
 *   Advanced (collapsible in streamlinedMode): Terms (ended_at, deposit) + Documents (uploads)
 */
export function LeaseFields({
  lease,
  defaultUnitId,
  defaultPropertyId,
  defaultCustomerId,
  onSave,
  onCancel,
  formId,
  streamlinedMode = false,
  prefillBanner,
  prefillDefaults,
  onSubmittingChange,
  className,
}: LeaseFieldsProps) {
  const { t, i18n } = useTranslation('leases')
  const { t: tc } = useTranslation('common')
  const { t: tCon } = useTranslation('contracts')

  const isEdit = !!lease
  // EP-59 V4 — on condominium accounts, the form is for OwnerContract
  // records: no rent / no deposit (zero by validator on the API side),
  // optional end date, transfer-date copy, ownership-document labels.
  const { data: accountSettings } = useAccountSettings()
  const isCondominium = accountSettings?.account_type === 'condominium'
  const { format, timezone, calendar } = useLocalizationConfig()

  const translateError = useApiErrorTranslator('leases')
  const addToast = useNotificationStore((s) => s.addToast)
  // EP-73 Tier-1: form_started:lease / form_submitted:lease lifecycle.
  const { markStarted, markSubmitted } = useFormTelemetry('lease')

  const createLease = useCreateLease()
  const updateLease = useUpdateLease()

  const { data: propertiesData } = useProperties()

  // Customer picker: server search + page accumulation (EP-116 T006). A broad
  // query still truncated at 20 with no way to reach row 21 onward — mirrors
  // the ParcelFormPage/AnnouncementFormPage pattern.
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerPage, setCustomerPage] = useState(1)
  const [accumulatedCustomers, setAccumulatedCustomers] = useState<Customer[]>([])
  const { data: customersData, isLoading: isLoadingCustomers } = useCustomers(customerPage, undefined, customerSearch)

  // A new query starts a new list; anything else appends the next page.
  useEffect(() => {
    setCustomerPage(1)
    setAccumulatedCustomers([])
  }, [customerSearch])

  useEffect(() => {
    if (!customersData?.data) return
    setAccumulatedCustomers((prev) => {
      if (customerPage === 1) return customersData.data
      const existingIds = new Set(prev.map((c) => c.id))
      return [...prev, ...customersData.data.filter((c) => !existingIds.has(c.id))]
    })
  }, [customersData, customerPage])

  // Create mode only (edit mode shows the customer as a locked read-only row,
  // below). The onboarding wizard (and any other caller) can pre-select a
  // customer via defaultCustomerId that sits outside the loaded/searched
  // page — without a direct fetch the picker falls back to its placeholder
  // and an unrelated save would silently retarget the lease's customer. Same
  // fallback-fetch shape as InvoiceFields' externalCustomer / VehicleFormPage's
  // fallback lease: gate the query on id=0 when not needed.
  const needFallbackCustomer =
    !isEdit && !!defaultCustomerId && !accumulatedCustomers.some((c) => c.id === defaultCustomerId)
  const { data: fallbackCustomer } = useCustomer(needFallbackCustomer && defaultCustomerId ? defaultCustomerId : 0)

  const { data: templatesData, isLoading: isLoadingTemplates } = useDocumentTemplates(1, 'lease')
  const defaultTemplateId = templatesData?.data?.[0]?.id

  const [selectedPropertyId, setSelectedPropertyId] = useState<number>(0)
  const [isCreating, setIsCreating] = useState(false)

  // Report mutation-pending so the wizard footer Save can show a spinner.
  useEffect(() => {
    onSubmittingChange?.(createLease.isPending || updateLease.isPending || isCreating)
  }, [createLease.isPending, updateLease.isPending, isCreating, onSubmittingChange])
  const [isPreviewingPdf, setIsPreviewingPdf] = useState(false)
  const [localImageUrls, setLocalImageUrls] = useState<string[] | null>(null)
  const [localAttachmentUrls, setLocalAttachmentUrls] = useState<string[] | null>(null)

  // EP-86 S2: Advanced section open/close state.
  // Starts collapsed when streamlinedMode=true; open otherwise.
  // Advanced (Terms + Documents) is shown only on the standalone pages. In the
  // onboarding wizard (streamlinedMode) it's hidden entirely — no toggle — so
  // step 5 stays essentials-only; the fields stay registered (display:none) with
  // their defaults (auto-filled deposit, +1yr ended_at) so the lease still saves.
  const advancedOpen = !streamlinedMode

  const today = localToday(timezone)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    getValues,
    formState: { errors, isDirty },
  } = useForm<LeaseFormData>({
    defaultValues: {
      unit_id: defaultUnitId ?? 0,
      customer_id: defaultCustomerId ?? 0,
      started_at: today,
      // S2-2: ended_at defaults to +1yr so collapsed Advanced never blocks submit.
      ended_at: addOneYear(today),
      // S2-2: deposit defaults to 0 (valid — see deposit registration below).
      deposit: 0,
      // T156-010: '' = not set, never 0. Owner 2026-10-08: on the standalone
      // create form ค่าแรกเข้า defaults to one month's rent (set when a unit is
      // picked, below) and the key-card deposit to ฿200. The wizard
      // (streamlinedMode) hides these fields, so it keeps them empty rather
      // than saving amounts nobody saw.
      move_in_fee_amount: '',
      keycard_deposit_amount: !isEdit && !streamlinedMode ? String(DEFAULT_KEYCARD_DEPOSIT) : '',
    },
  })

  // WEB-M3: warn before tab close/refresh with unsaved edits (beforeunload).
  useUnsavedChangesWarning(isDirty)

  const selectedUnitId = watch('unit_id')
  const selectedCustomerId = watch('customer_id')

  const imageUrls = localImageUrls ?? (isEdit && lease ? (lease.image_urls ?? []) : [])
  const attachmentUrls = localAttachmentUrls ?? (isEdit && lease ? (lease.attachment_urls ?? []) : [])
  // EP-160 S3: a FlowRent support session gets no image or attachment keys
  // (`<key>_present` only). The lists are shown hidden and never sent back:
  // an empty list sent back would erase the stored ones, and the setup scope
  // refuses the field outright (422 scope_setup_no_tenants).
  const imagesHidden = isEdit && removedInSession(lease, 'image_urls')
  const attachmentsHidden = isEdit && removedInSession(lease, 'attachment_urls')

  // -------------------------------------------------------------------------
  // Edit mode: pre-fill form from lease
  // -------------------------------------------------------------------------
  // Fill the form ONCE per lease. propertiesData and lease both refetch in
  // the background (staleTime, window focus), and resetting on every refetch
  // silently replaced what the operator was typing with the saved values
  // (found 2026-10-08 while testing the move-in defaults).
  const prefilledLeaseIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (isEdit && lease && prefilledLeaseIdRef.current !== lease.id) {
      prefilledLeaseIdRef.current = lease.id
      reset({
        unit_id: lease.unit_id,
        customer_id: lease.customer_id,
        started_at: lease.started_at.split('T')[0],
        ended_at: lease.ended_at?.split('T')[0] || '',
        deposit: lease.deposit,
        // T156-010: null/undefined → '' (not set), never '0'.
        move_in_fee_amount: lease.move_in_fee_amount != null ? String(lease.move_in_fee_amount) : '',
        keycard_deposit_amount: lease.keycard_deposit_amount != null ? String(lease.keycard_deposit_amount) : '',
      })
    }
  }, [isEdit, lease, reset])

  // Edit mode: select the lease's building once the properties are loaded.
  useEffect(() => {
    if (isEdit && lease) {
      if (lease.unit && propertiesData?.data) {
        const property = propertiesData.data.find((p: Property) =>
          p.room_types?.some((rt) => rt.units?.some((u) => u.id === lease.unit_id))
        )
        if (property) {
          setSelectedPropertyId(property.id)
        }
      }
    }
  }, [isEdit, lease, propertiesData])

  // -------------------------------------------------------------------------
  // Create mode: when defaultUnitId is given, find + set its parent property
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isEdit && defaultUnitId && propertiesData?.data) {
      const property = propertiesData.data.find((p: Property) =>
        p.room_types?.some((rt) => rt.units?.some((u) => u.id === defaultUnitId))
      )
      if (property) {
        setSelectedPropertyId(property.id)
      }
    }
  }, [isEdit, defaultUnitId, propertiesData])

  // Create mode: pre-select the property (without a unit) when the caller fixes
  // it (onboarding wizard). Unit stays unselected until auto-select or a manual
  // pick below.
  useEffect(() => {
    if (!isEdit && defaultPropertyId) {
      setSelectedPropertyId((prev) => prev || defaultPropertyId)
    }
  }, [isEdit, defaultPropertyId])

  // -------------------------------------------------------------------------
  // Unit picker (EP-116 T013): server search + page accumulation over
  // usePropertyUnits, mirroring AnnouncementFormPage's unit picker. Create
  // mode only — edit mode shows the unit as a locked read-only row below (the
  // same isEdit pattern as the customer field) and never fetches units at
  // all (propertyId gated to 0 disables the query).
  //
  // This REPLACES a client-derived list (property.room_types[].units[], every
  // unit present at any count, no truncation) with a paginated one — the one
  // place in this conversion that can make things WORSE, not better, if done
  // naively. Two things guard against that:
  //   1. vacant-eligibility filtering (below) stays client-side because the
  //      Units search endpoint can't express a status filter server-side
  //      (api/app/controllers/api/property_units_controller.rb's search
  //      branch only scopes by property_id, unlike the bare-page branch which
  //      does support params[:status] — verified 2026-08-06). This is a
  //      business-rule filter, the same category as the "Unnamed" customer
  //      exclusion above, not a second pass over the user's search text.
  //   2. the auto-advance effect further down: a raw fetched page can
  //      legitimately be all-occupied (zero eligible units after filtering)
  //      while a later page holds vacant ones — that must never render as
  //      "no results".
  const [unitSearch, setUnitSearch] = useState('')
  const [unitPage, setUnitPage] = useState(1)
  const [accumulatedUnits, setAccumulatedUnits] = useState<Unit[]>([])
  const { data: unitsData, isLoading: isLoadingUnits } = usePropertyUnits(
    !isEdit ? selectedPropertyId : 0, unitPage, undefined, unitSearch || undefined,
  )

  // A new query (search OR property change) starts a new list; anything else
  // appends the next page.
  useEffect(() => {
    setUnitPage(1)
    setAccumulatedUnits([])
  }, [unitSearch, selectedPropertyId])

  useEffect(() => {
    if (!unitsData?.data) return
    setAccumulatedUnits((prev) => {
      if (unitPage === 1) return unitsData.data
      const existingIds = new Set(prev.map((u) => u.id))
      return [...prev, ...unitsData.data.filter((u) => !existingIds.has(u.id))]
    })
  }, [unitsData, unitPage])

  // Create mode: a defaultUnitId (wizard preselection) may sit outside the
  // loaded/searched page — fetch it directly so it still resolves to a real
  // label instead of silently reading as unselected. Same fallback-fetch
  // shape as the customer picker above.
  const needFallbackUnit =
    !isEdit && !!defaultUnitId && !accumulatedUnits.some((u) => u.id === defaultUnitId)
  const { data: fallbackUnit } = useUnit(needFallbackUnit && defaultUnitId ? defaultUnitId : 0)

  // Memoized: availableUnits feeds two effects below (auto-select-if-single,
  // deposit auto-fill) by reference. A non-memoized value here would hand
  // those effects a new array identity every render and re-fire them forever
  // — the fresh-object-per-render trap, just inside the component instead of
  // a test mock this time.
  const availableUnits = useMemo(() => {
    // defaultUnitId is always force-included even if occupied — preserves
    // the pre-existing rule (a preselected unit must render, whatever its
    // status turns out to be).
    // EP-136 T136-020 — a reserved unit is already held by a paid/billed
    // booking fee, so it must NOT appear in the lease-create unit picker.
    // The exact `=== 'vacant'` check already excludes it with no change.
    const eligible = accumulatedUnits.filter((u) => u.status === 'vacant' || u.id === defaultUnitId)
    if (needFallbackUnit && fallbackUnit && !eligible.some((u) => u.id === fallbackUnit.id)) {
      return [fallbackUnit, ...eligible]
    }
    return eligible
  }, [accumulatedUnits, defaultUnitId, needFallbackUnit, fallbackUnit])

  // Keep advancing automatically while the JUST-FETCHED page's own eligible
  // set is empty but the server says another page exists — otherwise a page
  // that happens to be all-occupied would render as "no vacant units" while
  // later pages hold real ones.
  //
  // Deliberately checked against unitsData.data directly, NOT availableUnits:
  // availableUnits is sourced from accumulatedUnits, which only catches up to
  // a fresh unitsData one render later (accumulatedUnits is set by the
  // useEffect above, not at render time) — checking availableUnits here would
  // read last render's stale (still-empty) value in the same pass unitsData
  // first reflects the new page, firing a spurious advance even when the
  // just-arrived page already has eligible units. unitsData itself has no
  // such lag: it comes straight from the hook at render time.
  const unitsCurrentPage = unitsData?.meta?.current_page
  const unitsTotalPages = unitsData?.meta?.total_pages
  // EP-136 T136-020 — a page whose units are ALL reserved (held by a paid/
  // billed booking fee, no vacant units) must be treated exactly like an
  // all-occupied page: `currentPageHasEligibleUnit` reads false and the
  // effect below keeps auto-advancing to the next page instead of silently
  // stopping and reporting "no eligible units". The exact `=== 'vacant'`
  // check already excludes 'reserved' with no change needed; pinned by a
  // vitest below.
  const currentPageHasEligibleUnit = (unitsData?.data ?? []).some(
    (u) => u.status === 'vacant' || u.id === defaultUnitId
  )
  useEffect(() => {
    if (isEdit) return
    if (currentPageHasEligibleUnit) return
    if (unitsCurrentPage === undefined || unitsTotalPages === undefined) return
    if (unitsCurrentPage >= unitsTotalPages) return
    setUnitPage((p) => p + 1)
  }, [isEdit, currentPageHasEligibleUnit, unitsCurrentPage, unitsTotalPages])

  // Create mode: auto-select the unit when the selected property has exactly one
  // vacant unit (found so far); when there are several, leave it unpicked so
  // the operator chooses.
  useEffect(() => {
    if (isEdit) return
    if (!selectedUnitId && availableUnits.length === 1) {
      setValue('unit_id', availableUnits[0].id)
    }
  }, [isEdit, availableUnits, selectedUnitId, setValue])

  // -------------------------------------------------------------------------
  // Prefill from AI import (task-030 Apply path — create mode only)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isEdit && prefillDefaults && Object.keys(prefillDefaults).length > 0) {
      reset((current) => ({ ...current, ...prefillDefaults }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillDefaults])

  // -------------------------------------------------------------------------
  // Auto-fill deposit when unit is selected (create mode only)
  // EP-59 V4 — skip the auto-fill for condominium accounts so deposit
  // stays at 0 (the only value OwnerContract accepts).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (isCondominium) {
      setValue('deposit', 0)
      // Condominium accounts hide both move-in fields, so no hidden default.
      setValue('move_in_fee_amount', '')
      setValue('keycard_deposit_amount', '')
      return
    }
    if (!isEdit && selectedUnitId && selectedPropertyId && propertiesData?.data) {
      const property = propertiesData.data.find((p: Property) => p.id === selectedPropertyId)
      const unit = availableUnits.find((u) => u.id === Number(selectedUnitId))
      if (property && unit) {
        const depositType = property.deposit_type || 'months'
        const depositValue = property.deposit_value ?? 1
        if (depositType === 'months') {
          setValue('deposit', unit.price * depositValue)
        } else {
          setValue('deposit', depositValue)
        }
        // Owner 2026-10-08: ค่าแรกเข้า defaults to one month's rent, refreshed
        // with the unit like the deposit; still editable or clearable.
        if (!streamlinedMode) {
          setValue('move_in_fee_amount', String(unit.price))
        }
      }
    }
  }, [selectedUnitId, selectedPropertyId, availableUnits, propertiesData, setValue, isEdit, isCondominium, streamlinedMode])

  // -------------------------------------------------------------------------
  // Track the just-saved lease during a Preview so subsequent clicks UPDATE
  // the same DB row instead of POSTing again. Save & Continue reads the
  // same value so it doesn't double-create either.
  // -------------------------------------------------------------------------
  const [previewSavedLease, setPreviewSavedLease] = useState<Lease | null>(null)

  // Shared persistence helper used by both Save & Continue and Preview.
  // Returns the live saved Lease (creates first time, patches on re-use).
  const persistLease = async (data: LeaseFormData): Promise<Lease> => {
    if (isEdit && lease) {
      return await updateLease.mutateAsync({
        id: lease.id,
        started_at: data.started_at,
        ended_at: data.ended_at || undefined,
        deposit: data.deposit,
        move_in_fee_amount: parseOptionalAmount(data.move_in_fee_amount),
        keycard_deposit_amount: parseOptionalAmount(data.keycard_deposit_amount),
        ...(imagesHidden ? {} : { image_urls: imageUrls }),
        ...(attachmentsHidden ? {} : { attachment_urls: attachmentUrls }),
      })
    }
    if (previewSavedLease) {
      // Re-click: PATCH the existing row with the current form values
      const updated = await updateLease.mutateAsync({
        id: previewSavedLease.id,
        started_at: data.started_at,
        ended_at: data.ended_at || undefined,
        deposit: data.deposit,
        move_in_fee_amount: parseOptionalAmount(data.move_in_fee_amount),
        keycard_deposit_amount: parseOptionalAmount(data.keycard_deposit_amount),
        image_urls: imageUrls,
        attachment_urls: attachmentUrls,
      })
      setPreviewSavedLease(updated)
      return updated
    }
    setIsCreating(true)
    try {
      const result = await createLease.mutateAsync({
        unit_id: Number(data.unit_id),
        customer_id: Number(data.customer_id),
        document_template_id: defaultTemplateId,
        started_at: data.started_at,
        ended_at: data.ended_at || undefined,
        deposit: data.deposit,
        move_in_fee_amount: parseOptionalAmount(data.move_in_fee_amount),
        keycard_deposit_amount: parseOptionalAmount(data.keycard_deposit_amount),
      })
      // Generate and upload the lease PDF using the assigned template.
      try {
        const { data: fullLease } = await api.get<ApiResponse<Lease>>(`/api/leases/${result.id}`)
        const template = fullLease.data.document_template?.template as Template | undefined
        if (!template) throw new Error('No lease document template found. Please configure a lease template in Settings → Templates.')
        const { generateAndUploadLeasePdf } = await import('@/lib/pdfGenerator')
        const pdfKey = await generateAndUploadLeasePdf(fullLease.data, template)
        const updated = await updateLease.mutateAsync({ id: result.id, pdf_key: pdfKey })
        setPreviewSavedLease(updated)
        return updated
      } catch (pdfError) {
        console.error('Failed to generate/upload lease PDF:', pdfError)
        // The lease itself is saved — return it even if the PDF step failed.
        setPreviewSavedLease(result)
        return result
      }
    } finally {
      setIsCreating(false)
    }
  }

  // -------------------------------------------------------------------------
  // Submit — routes through `persistLease()` so Save & Continue and Preview
  // share one save path. If Preview already created the lease, this UPDATEs
  // the same row instead of POSTing a duplicate.
  //
  // F1/AC-1 (EP-158): when embedded via an external formId (the onboarding
  // wizard) AND editing AND nothing changed, skip the PATCH entirely —
  // isDirty is enough here (not just in general) because streamlinedMode
  // keeps every Advanced field (attachments, etc.) registered with its
  // loaded default even while visually hidden, so nothing can become dirty
  // without going through a field RHF is already watching. Gated on formId
  // so a standalone /leases/:id/edit page's explicit Save click still PATCHes
  // even with no changes (its own deliberate action, not a step revisit).
  // -------------------------------------------------------------------------
  const onSubmit = async (data: LeaseFormData) => {
    if (isEdit && !!formId && !isDirty && lease) {
      onSave(lease)
      return
    }
    try {
      const saved = await persistLease(data)
      markSubmitted()
      onSave(saved)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  // -------------------------------------------------------------------------
  // Preview PDF — wizard mode only. Saves the lease (or updates the saved
  // one), then opens the rendered PDF so the user reviews the real
  // persisted contract document, not a draft.
  // -------------------------------------------------------------------------
  const handlePreviewPdf = async () => {
    const tmpl = templatesData?.data?.[0]?.template as Template | undefined
    if (!tmpl) {
      addToast({ type: 'error', message: t('messages.no_lease_template') })
      return
    }
    const values = getValues()
    // BUG-032: this used to return silently when the lease wasn't fully filled
    // (no unit/customer/start date) — the operator pressed Preview PDF and
    // "nothing happened". Surface what's missing instead of a dead no-op.
    if (!values.unit_id || !values.customer_id || !values.started_at) {
      addToast({ type: 'info', message: t('messages.preview_requires_fields') })
      return
    }

    setIsPreviewingPdf(true)
    try {
      const saved = await persistLease(values)
      // persistLease returns the shallow updated lease (no nested unit /
      // customer / room_type.property). previewLeasePdf regenerates the doc
      // client-side from those associations, so re-fetch the full lease first —
      // otherwise the preview shows a blank tenant/room/building and ฿0 rent
      // (deposit is the only direct attribute that survives the shallow payload).
      const { data: full } = await api.get<ApiResponse<Lease>>(`/api/leases/${saved.id}`)
      const { previewLeasePdf } = await import('@/lib/pdfGenerator')
      await previewLeasePdf(full.data, tmpl)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    } finally {
      setIsPreviewingPdf(false)
    }
  }

  // -------------------------------------------------------------------------
  // Derived display values
  // -------------------------------------------------------------------------
  const propertyOptions =
    propertiesData?.data?.map((p: Property) => ({ value: String(p.id), label: p.name })) || []

  const unitOptions = availableUnits.map((u) => ({
    value: String(u.id),
    label: `${u.name} - ${formatBaht(u.price)}${t('labels.per_month')}`,
  }))

  // Filter out the API-side "Unnamed" fallback (Customer#name returns the
  // literal string "Unnamed" when first_name/last_name/phone/email are all
  // blank). Selecting such a customer leaks the placeholder into invoices
  // and lease docs — the user explicitly asked us not to surface them. This
  // is a permanent data-quality rule, not a second pass over the user's
  // search text — the accumulated list is never re-filtered by customerSearch.
  const customerOptions = (() => {
    const opts = accumulatedCustomers
      .filter((c) => c.name && c.name !== 'Unnamed')
      .map((c) => ({ value: String(c.id), label: customerLabel(c) }))
    if (needFallbackCustomer && fallbackCustomer && !opts.some((o) => o.value === String(fallbackCustomer.id))) {
      opts.push({ value: String(fallbackCustomer.id), label: customerLabel(fallbackCustomer) })
    }
    return opts
  })()

  // Find selected customer for the info card (create mode only) — search the
  // accumulated pages first, then the fallback fetch.
  const selectedCustomer = !isEdit && selectedCustomerId
    ? (accumulatedCustomers.find((c) => c.id === selectedCustomerId) ??
       (fallbackCustomer?.id === selectedCustomerId ? fallbackCustomer : undefined))
    : undefined

  // Rent display: find the currently selected unit's price
  const selectedUnit = availableUnits.find((u) => u.id === Number(selectedUnitId))
  const rentAmount = isEdit ? lease?.unit?.price : selectedUnit?.price

  // Deposit hint from selected property
  const selectedProperty = propertiesData?.data?.find((p: Property) => p.id === selectedPropertyId)
  const depositHint = selectedProperty
    ? selectedProperty.deposit_type === 'months'
      ? t('messages.deposit_months', { count: selectedProperty.deposit_value })
      : t('messages.deposit_fixed', { amount: formatBaht(Number(selectedProperty.deposit_value), { withSymbol: false }) })
    : undefined

  // Whether default* props actually pre-filled a field. The badge itself
  // is also gated on wizard mode (`formId != null`) at render time — the
  // "filled from previous step" label only makes sense in the multi-step
  // wizard, not on a standalone /leases/new page (user feedback).
  const showPreFilledBadges = !!formId
  const unitPreFilled = showPreFilledBadges && !isEdit && !!defaultUnitId
  const customerPreFilled = showPreFilledBadges && !isEdit && !!defaultCustomerId
  // Property is always derived from the selected unit: show badge when unit is pre-filled.
  const propertyPreFilled = unitPreFilled

  return (
    <Card className={className}>
      {/* Apply-from-file banner (task-030) — rendered above the form */}
      {prefillBanner}

      {isCreating && (
        <div className="absolute inset-0 bg-base-100/80 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm font-medium">{t('messages.creating')}</p>
        </div>
      )}

      <form id={formId} onSubmit={handleSubmit(onSubmit)} onChangeCapture={markStarted} className="space-y-6">

        {/* ================================================================
            ESSENTIALS SECTION
            Always visible: customer + property/unit selectors, rent display,
            and started_at. These are the minimum required fields.
            ================================================================ */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Customer field */}
          {isEdit ? (
            <div>
              <label className="label">
                <span className="label-text">{t('labels.customer')}</span>
              </label>
              <div className="input input-bordered flex items-center bg-base-200">
                {lease?.customer?.name || t('messages.unknown')}
              </div>
            </div>
          ) : (
            <div>
              {/* Custom label with PreFilledBadge when customer is pre-selected */}
              <div className="label">
                <span className="label-text flex items-center gap-1">
                  {t('labels.customer')}
                  {customerPreFilled && <PreFilledBadge className="ml-1" />}
                </span>
              </div>
              <Controller
                name="customer_id"
                control={control}
                rules={{ required: true, validate: (v) => v !== 0 }}
                render={({ field, fieldState }) => (
                  <SearchableDropdown
                    placeholder={t('placeholders.search_customers')}
                    options={customerOptions}
                    value={String(field.value)}
                    onChange={(val) => field.onChange(Number(val))}
                    onSearch={setCustomerSearch}
                    isLoading={isLoadingCustomers}
                    error={fieldState.error?.message}
                    hasMore={
                      !!customersData?.meta &&
                      customersData.meta.current_page < customersData.meta.total_pages
                    }
                    onLoadMore={() => setCustomerPage((p) => p + 1)}
                  />
                )}
              />
            </div>
          )}

          {/* Customer info card */}
          {selectedCustomer && (
            <div className="md:col-span-2 p-4 rounded-lg bg-base-200 border border-base-300 text-sm space-y-1">
              {selectedCustomer.phone && (
                <div className="flex gap-2">
                  <span className="text-base-content/60 w-20 shrink-0">{t('labels.phone')}</span>
                  <span className="font-medium">{selectedCustomer.phone}</span>
                </div>
              )}
              {selectedCustomer.id_card_number && (
                <div className="flex gap-2">
                  <span className="text-base-content/60 w-20 shrink-0">{t('labels.id_card')}</span>
                  <span className="font-medium">{selectedCustomer.id_card_number}</span>
                </div>
              )}
              {selectedCustomer.address && (
                <div className="flex gap-2">
                  <span className="text-base-content/60 w-20 shrink-0">{t('labels.address')}</span>
                  <span className="font-medium whitespace-pre-wrap">{selectedCustomer.address}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Property / unit / rent / start date. Separate grid so property+unit
            pair on one row and rent+start-date on the next — like fields align
            (badged property/unit together, plain rent/date together) instead of
            a tall property+unit bundle sitting beside a short rent field. */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Unit / Property field */}
          {isEdit ? (
            <div>
              <label className="label">
                <span className="label-text">{t('labels.unit')}</span>
              </label>
              <div className="input input-bordered flex items-center bg-base-200">
                {lease?.unit?.name || t('messages.unknown')} - {formatBaht(lease?.unit?.price ?? 0)}{t('labels.per_month')}
              </div>
            </div>
          ) : (
            <>
              {/* Property — own grid cell */}
              <div>
                {/* Custom property label with PreFilledBadge when derived from pre-selected unit */}
                <div className="label">
                  <span className="label-text flex items-center gap-1">
                    {t('labels.property')}
                    {propertyPreFilled && <PreFilledBadge className="ml-1" />}
                  </span>
                </div>
                <SearchableDropdown
                  placeholder={t('placeholders.search_properties')}
                  options={propertyOptions}
                  value={String(selectedPropertyId)}
                  onChange={(val) => {
                    const newPropertyId = Number(val)
                    // Only wipe unit selection when the property actually changes
                    // — re-selecting the currently-selected property (e.g. from
                    // the onboarding wizard auto-fill flow) must preserve the
                    // pre-selected unit.
                    if (newPropertyId !== selectedPropertyId) {
                      setSelectedPropertyId(newPropertyId)
                      setValue('unit_id', 0)
                      // A unit search typed for the previous building must not
                      // silently scope the new one.
                      setUnitSearch('')
                    }
                  }}
                />
              </div>
              {/* Unit — own grid cell */}
              <div>
                {/* Custom unit label with PreFilledBadge when unit is pre-selected */}
                <div className="label">
                  <span className="label-text flex items-center gap-1">
                    {t('labels.unit')}
                    {unitPreFilled && <PreFilledBadge className="ml-1" />}
                  </span>
                </div>
                <Controller
                  name="unit_id"
                  control={control}
                  // An available (vacant) unit must be selected to proceed — show
                  // a clear validation error on Save when none is chosen.
                  rules={{ validate: (v) => v !== 0 || t('errors.unit_required') }}
                  render={({ field, fieldState }) => (
                    <SearchableDropdown
                      placeholder={t('placeholders.select_unit')}
                      options={unitOptions}
                      value={String(field.value)}
                      onChange={(val) => field.onChange(Number(val))}
                      onSearch={setUnitSearch}
                      isLoading={isLoadingUnits}
                      error={fieldState.error?.message}
                      disabled={!selectedPropertyId || availableUnits.length === 0}
                      hasMore={
                        !!unitsData?.meta &&
                        unitsData.meta.current_page < unitsData.meta.total_pages
                      }
                      onLoadMore={() => setUnitPage((p) => p + 1)}
                    />
                  )}
                />
                {!!selectedPropertyId && availableUnits.length === 0 && (
                  <p className="text-sm text-warning">{t('empty.no_vacant_units')}</p>
                )}
              </div>
            </>
          )}

          {/* Rent display — read-only, derived from selected unit (Essentials: inherited rent) */}
          {rentAmount !== undefined && (
            <div data-tour-target="lease-rent-display">
              <label className="label">
                <span className="label-text">{t('labels.monthly_rent')}</span>
              </label>
              <div className="input input-bordered flex items-center bg-base-200">
                {formatBaht(rentAmount)}{t('labels.per_month')}
              </div>
            </div>
          )}

          {/* Start date — Essentials (required) */}
          <div data-tour-target="lease-date-range">
            <DateInput
              label={isCondominium ? tCon('date_label.start.ownership') : tCon('date_label.start.rental')}
              error={errors.started_at?.message}
              defaultValue={isEdit && lease ? lease.started_at.split('T')[0] : today}
              dateFormat={format}
              timezone={timezone}
              calendar={calendar}
              locale={i18n.language}
              {...register('started_at', { required: true })}
            />
          </div>
        </div>

        {/* ================================================================
            ADVANCED SECTION
            streamlinedMode=true: collapsed by default, toggle to expand.
            streamlinedMode=false: always visible (section heading only).

            S2-2: content is always rendered (display:none, not removed) so
            RHF keeps fields registered with their defaults. Collapsed
            Advanced never produces a hidden-required validation error.
            ================================================================ */}

        {/* Advanced content — shown on standalone pages; hidden (display:none)
            in the wizard (streamlinedMode) with no toggle. Kept in the DOM so
            RHF keeps deposit / ended_at / uploads registered with their defaults. */}
        <div
          id="lease-advanced-section"
          style={{ display: !streamlinedMode || advancedOpen ? 'block' : 'none' }}
          className="space-y-6"
        >
          {/* Plain section heading in standalone (non-streamlined) mode */}
          {!streamlinedMode && (
            <div className="border-t border-base-300 pt-4">
              <p className="text-sm font-semibold text-base-content/60">
                {t('sections.advanced_options')}
              </p>
            </div>
          )}

          {/* ---- Terms sub-group: ended_at + deposit ---- */}
          <div className="space-y-1">
            <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wide">
              {t('sections.terms')}
            </p>
            <div className="grid gap-6 md:grid-cols-2">
              <DateInput
                label={isCondominium ? tCon('date_label.end.ownership') : tCon('date_label.end.rental')}
                error={errors.ended_at?.message}
                defaultValue={isEdit && lease ? (lease.ended_at?.split('T')[0] || '') : addOneYear(today)}
                dateFormat={format}
                timezone={timezone}
                calendar={calendar}
                locale={i18n.language}
                // LSE-1: a lease or leasehold can legitimately end far beyond
                // today + 2 years and the server accepts it; only the
                // 2000-01-01 floor (DateInput's default min) applies here.
                unboundedMax
                {...register('ended_at')}
              />

              {/* EP-59 V4 — deposit input hidden for condominium accounts.
                  OwnerContract records always have deposit = 0 (validated
                  server-side by the equal_to: 0 numericality validator).
                  S2-2: validate (not required: true) so deposit=0 default
                  never blocks a collapsed-Advanced submit. */}
              {!isCondominium && (
                <div>
                  <Input
                    label={`${t('labels.deposit')} (฿)`}
                    type="number"
                    error={errors.deposit?.message}
                    {...register('deposit', {
                      valueAsNumber: true,
                      // S2-2: 0 is a valid deposit (e.g. no-deposit unit).
                      // Only reject NaN (cleared input) and negatives.
                      validate: (v) => !isNaN(v),
                      min: { value: 0, message: '' },
                    })}
                  />
                  {depositHint && (
                    <p className="text-xs text-base-content/50 mt-1">{t('messages.property_default', { hint: depositHint })}</p>
                  )}
                </div>
              )}
            </div>

            {/* EP-156 (T156-010, §3.3) — two optional, invoicing-independent
                charges: ค่าแรกเข้า and a key-card deposit. Empty means not
                set (never 0); hidden alongside the deposit field for
                condominium accounts (the deposit-held, invoicing-driven
                conventions these charges piggyback on don't apply to
                OwnerContract). */}
            {!isCondominium && (
              <div className="grid gap-6 md:grid-cols-2">
                <Input
                  label={`${t('labels.move_in_fee')} (฿)`}
                  type="number"
                  error={errors.move_in_fee_amount?.message}
                  {...register('move_in_fee_amount')}
                />
                <Input
                  label={`${t('labels.keycard_deposit')} (฿)`}
                  type="number"
                  placeholder={t('placeholders.keycard_deposit_example')}
                  error={errors.keycard_deposit_amount?.message}
                  {...register('keycard_deposit_amount')}
                />
              </div>
            )}
          </div>

          {/* ---- Documents sub-group: images + attachments ---- */}
          <div className="space-y-4">
            <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wide">
              {t('sections.documents')}
            </p>

            {/* Image Uploads */}
            {imagesHidden ? (
              <div data-testid="lease-images-hidden">
                <p className="text-sm font-medium mb-2">{t('attachments.images_section')}</p>
                <SupportSessionHidden testId="lease-images-hidden-chip" />
              </div>
            ) : (
              <IdCopyUpload
                label={t('attachments.images_section')}
                value={imageUrls.map((key) =>
                  key.startsWith('/api/') || key.startsWith('http') ? key : `/api/images/${key}`
                )}
                onChange={(displayUrls) => {
                  const keys = displayUrls.map((url) => {
                    const match = url.match(/\/api\/images\/(.+)$/)
                    return match ? match[1] : url
                  })
                  setLocalImageUrls(keys)
                }}
                maxFiles={10}
                accept="image/*"
                onUpload={uploadLeaseFile}
              />
            )}

            {/* Attachment Uploads — relabelled for OwnerContract */}
            {attachmentsHidden ? (
              <div data-testid="lease-attachments-hidden">
                <p className="text-sm font-medium mb-2">
                  {isCondominium ? tCon('attachments.ownership') : t('attachments.files_section')}
                </p>
                <SupportSessionHidden testId="lease-attachments-hidden-chip" />
              </div>
            ) : (
              <IdCopyUpload
                label={isCondominium ? tCon('attachments.ownership') : t('attachments.files_section')}
                value={attachmentUrls.map((key) =>
                  key.startsWith('/api/') || key.startsWith('http') ? key : `/api/images/${key}`
                )}
                onChange={(displayUrls) => {
                  const keys = displayUrls.map((url) => {
                    const match = url.match(/\/api\/images\/(.+)$/)
                    return match ? match[1] : url
                  })
                  setLocalAttachmentUrls(keys)
                }}
                maxFiles={10}
                accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.doc,.docx"
                onUpload={uploadLeaseFile}
              />
            )}
          </div>
        </div>

        {/* Action row.
              • Wizard mode (formId set) → "Preview Lease PDF" — the wizard's
                bottom-bar Save & Continue handles the actual create.
              • Standalone mode → original create/update submit. */}
        {formId ? (
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              leftIcon={<FileText className="w-4 h-4" aria-hidden="true" />}
              onClick={handlePreviewPdf}
              loading={isPreviewingPdf}
              disabled={isLoadingTemplates || !defaultTemplateId}
              data-testid="lease-preview-pdf"
            >
              {t('actions.preview_pdf')}
            </Button>
          </div>
        ) : (
          <FormActions>
            {onCancel && (
              <Button type="button" variant="ghost" onClick={onCancel}>
                {tc('actions.cancel')}
              </Button>
            )}
            <Button
              type="submit"
              loading={createLease.isPending || updateLease.isPending || isCreating}
              disabled={!isEdit && (isLoadingTemplates || !defaultTemplateId)}
            >
              {isEdit ? tc('actions.save_changes') : t('actions.create_lease')}
            </Button>
          </FormActions>
        )}
      </form>
    </Card>
  )
}
